/**
 * Manufacturer catalogue (starter set): common Belgian residential modular devices from Hager,
 * Schneider Electric, ABB and Legrand, Niko wiring devices (sockets and switches, as the set of
 * parts a point is made of) and the Niko Home Control controller, each with the page it was
 * checked on. Electricians pick a
 * reference per device; the bill of materials and the board layout (module widths) use it.
 * The data is to be confirmed by users; `sourceUrl` says where each line comes from.
 */
import type { Element, Op, Project } from '@elec/domain';
import data from './catalog.json' with { type: 'json' };

export type CatalogKind =
  | 'breaker'
  | 'rcd'
  | 'rcbo'
  | 'switch-disconnector'
  | 'contactor'
  | 'impulse-relay'
  | 'spd'
  /** Wiring devices (points): a set of parts (mechanism, central plate, cover plate). */
  | 'socket'
  | 'switch'
  /** Home automation controller (Niko Home Control, KNX), on the DIN rail. */
  | 'home-automation';

/** Kinds that are points rather than devices of the board. */
export const POINT_KINDS: ReadonlySet<CatalogKind> = new Set(['socket', 'switch']);

export interface CatalogPart {
  reference: string;
  description: string;
  sourceUrl: string;
}

export interface CatalogItem {
  brand: string;
  range: string;
  reference: string;
  kind: CatalogKind;
  poles: '1P' | '1P+N' | '2P' | '3P' | '4P';
  ratingA: number | null;
  curve: 'B' | 'C' | 'D' | null;
  sensitivityMa: number | null;
  rcdType: string | null;
  breakingKa: number | null;
  /** Width in 17.5 mm modules, when the datasheet gives it. */
  modules: number | null;
  description: string;
  sourceUrl: string;
  /** A point made of several articles: one bill-of-materials line each. */
  parts?: CatalogPart[];
}

export const CATALOG: readonly CatalogItem[] = data as CatalogItem[];
export const BRANDS = [...new Set(CATALOG.map((c) => c.brand))];

/** Key stored on the element (`props.catalogRef`): "Hager MWN216". */
export const refKey = (c: Pick<CatalogItem, 'brand' | 'reference'>) => `${c.brand} ${c.reference}`;
export const findRef = (key: unknown): CatalogItem | undefined =>
  typeof key === 'string' ? CATALOG.find((c) => refKey(c) === key) : undefined;

const KIND_OF: Record<string, CatalogKind> = {
  'iec.breaker': 'breaker',
  'iec.breaker-2p': 'breaker',
  'iec.breaker-3p': 'breaker',
  'iec.breaker-4p': 'breaker',
  'iec.rcd': 'rcd',
  'iec.rcd-2p': 'rcd',
  'iec.rcd-4p': 'rcd',
  'iec.rcbo': 'rcbo',
  'iec.switch-disconnector': 'switch-disconnector',
  'iec.switch-disconnector-2p': 'switch-disconnector',
  'iec.switch-disconnector-3p': 'switch-disconnector',
  'iec.switch-disconnector-4p': 'switch-disconnector',
  'iec.contactor': 'contactor',
  'iec.coil-impulse': 'impulse-relay',
  'iec.spd': 'spd',
  'iec.socket': 'socket',
  'iec.switch': 'switch',
  'iec.home-automation': 'home-automation',
};
export const catalogKind = (el: Pick<Element, 'symbolId'>): CatalogKind | undefined => KIND_OF[el.symbolId];

const num = (v: unknown) =>
  typeof v === 'number' ? v : typeof v === 'string' && v !== '' ? Number(v) : undefined;

/** Pole count of an element: from its symbol (…-4p) or its `poles` property; 2 by default. */
export function polesOf(el: Pick<Element, 'symbolId' | 'props'>): number {
  const m = /-(\d)p$/.exec(el.symbolId);
  if (m) return Number(m[1]);
  return num(el.props.poles) ?? 2;
}
const poleMatch = (item: CatalogItem, poles: number) =>
  poles >= 3
    ? item.poles === `${poles}P`
    : item.poles === '2P' || item.poles === '1P+N' || item.poles === '1P';

/** Catalogue lines that match an element's characteristics (kind, poles, rating, curve, sensitivity, type). */
export function matchesFor(el: Pick<Element, 'symbolId' | 'props'>, brand?: string): CatalogItem[] {
  const kind = catalogKind(el);
  if (!kind) return [];
  // Points and controllers: by kind and brand only.
  if (POINT_KINDS.has(kind) || kind === 'home-automation')
    return CATALOG.filter((c) => c.kind === kind && (!brand || c.brand === brand));
  const poles = polesOf(el);
  const rating = num(el.props.ratingA);
  const curve = kind === 'breaker' || kind === 'rcbo' ? String(el.props.curve ?? 'C') : undefined;
  const sens = kind === 'rcd' || kind === 'rcbo' ? num(el.props.sensitivityMa) : undefined;
  const type = kind === 'rcd' || kind === 'rcbo' ? String(el.props.rcdType ?? 'A') : undefined;
  return CATALOG.filter(
    (c) =>
      c.kind === kind &&
      (kind === 'impulse-relay' || kind === 'spd' || poleMatch(c, poles)) &&
      (!brand || c.brand === brand) &&
      (rating === undefined || c.ratingA === null || c.ratingA === rating) &&
      (!curve || c.curve === curve) &&
      (sens === undefined || c.sensitivityMa === sens) &&
      (!type || c.rcdType === type),
  );
}

/** Width in modules of a device on the DIN rail: from its catalogue reference, else typical. */
export function modulesOf(el: Pick<Element, 'symbolId' | 'props'>): number {
  const item = findRef(el.props.catalogRef);
  if (item?.modules) return item.modules;
  const kind = catalogKind(el);
  const poles = polesOf(el);
  switch (kind) {
    case 'breaker':
      return poles >= 3 ? poles : item?.poles === '1P+N' ? 1 : 2;
    case 'rcd':
    case 'switch-disconnector':
      return poles >= 3 ? 4 : 2;
    case 'rcbo':
      return 2;
    case 'contactor':
      return 2;
    case 'impulse-relay':
      return 1;
    case 'spd':
      return 2;
    case 'home-automation':
      return 6;
    default:
      return 0;
  }
}

/** A brand whose catalogue has wiring devices but no protective devices (Niko). */
const brandIsPointsOnly = (brand: string) =>
  !CATALOG.some((c) => c.brand === brand && !POINT_KINDS.has(c.kind) && c.kind !== 'home-automation');

/**
 * Ops giving every device of the project without a reference the first matching reference of a
 * brand. Returns also the devices for which the brand has nothing that matches.
 */
export function assignBrandOps(
  project: Project,
  brand: string,
  overwrite = false,
): { ops: Op[]; missing: Element[] } {
  const ops: Op[] = [];
  const missing: Element[] = [];
  for (const el of Object.values(project.elements)) {
    if (project.sheets[el.sheetId]?.kind !== 'single-line' || !catalogKind(el)) continue;
    if (el.props.catalogRef && !overwrite) continue;
    const kind = catalogKind(el)!;
    const m = matchesFor(el, brand)[0];
    if (!m) {
      // A brand without wiring devices (or without board devices, Niko) leaves those alone.
      if (!POINT_KINDS.has(kind) && kind !== 'home-automation' && !brandIsPointsOnly(brand)) missing.push(el);
      continue;
    }
    ops.push({
      op: 'update',
      coll: 'elements',
      id: el.id,
      patch: { props: { ...el.props, catalogRef: refKey(m) } },
    });
  }
  return { ops, missing };
}

export * from './prices.ts';
